import React, {useState, useEffect, useRef} from "react";
import {Avatar, Box, Button, Checkbox, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, Divider, FormControlLabel, IconButton, Stack, TextField, Tooltip, Typography} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ReplyIcon from "@mui/icons-material/Reply";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import CelebrationIcon from "@mui/icons-material/Celebration";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SentimentVerySatisfiedOutlinedIcon from "@mui/icons-material/SentimentVerySatisfiedOutlined";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import DoDisturbOutlinedIcon from "@mui/icons-material/DoDisturbOutlined";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import {Navigate, useParams} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";
import type {DiscussionTopic} from "../../../sdo/DiscussionTopic.ts";
import type {DiscussionPost} from "../../../sdo/DiscussionPost.ts";
import type {DiscussionVote} from "../../../sdo/DiscussionVote.ts";
import type {DiscussionPostComment} from "../../../sdo/DiscussionPostComment.ts";
import {
    DISCUSSION_VOTE_TYPES,
    LIKE_VOTE_TYPES,
    DISLIKE_VOTE_TYPES,
    voteTypeByName,
    type DiscussionVoteType,
} from "../../../sdo/DiscussionVoteType.ts";

// TODO: Remove once login flow is wired and appSession.getUsername() returns a real value in dev.
const DEV_USER = "dev-user";

const SPINE_COL = 48;
const AVATAR_SIZE = 36;
const CONNECT_OVERLAP = (SPINE_COL - AVATAR_SIZE) / 2;

// --- Vote icon registry ---

const VOTE_COLORS: Record<string, string> = {
    LIKE: "#02cfee",
    CELEBRATE: "#38ff82",
    INSIGHTFUL: "#ffd93f",
    SUPPORT: "#ff85a1",
    LOVE: "#ff3e6c",
    FUNNY: "#ffb347",
    DISLIKE: "#d83f2f",
    DISAGREE: "#e28b4c",
    INCORRECT: "#d83f2f",
};

const VOTE_OUTLINE_ICONS: Record<string, React.ElementType> = {
    LIKE: ThumbUpOutlinedIcon,
    CELEBRATE: CelebrationOutlinedIcon,
    INSIGHTFUL: LightbulbOutlinedIcon,
    SUPPORT: VolunteerActivismOutlinedIcon,
    LOVE: FavoriteBorderIcon,
    FUNNY: SentimentVerySatisfiedOutlinedIcon,
    DISLIKE: ThumbDownOutlinedIcon,
    DISAGREE: DoDisturbOutlinedIcon,
    INCORRECT: ReportProblemOutlinedIcon,
};

const VOTE_FILLED_ICONS: Record<string, React.ElementType> = {
    LIKE: ThumbUpIcon,
    CELEBRATE: CelebrationIcon,
    INSIGHTFUL: LightbulbIcon,
    SUPPORT: VolunteerActivismIcon,
    LOVE: FavoriteIcon,
    FUNNY: SentimentVerySatisfiedIcon,
    DISLIKE: ThumbDownIcon,
    DISAGREE: DoDisturbIcon,
    INCORRECT: ReportProblemIcon,
};

function voteIconComponent(name: string, filled: boolean): React.ElementType {
    const registry = filled ? VOTE_FILLED_ICONS : VOTE_OUTLINE_ICONS;
    return registry[name] ?? ThumbUpOutlinedIcon;
}

// --- Sample data ---

// TODO: Replace with API call to load topic by UID
function buildTestTopic(currentUser: string): DiscussionTopic {
    return {
        uid: "topic-1",
        createdAt: new Date(2026, 2, 15, 10, 0).getTime(),
        createdBy: currentUser,
        title: "Operational Security Protocols",
        description: "Discussion around updated OPSEC guidelines for field teams and remote analysts. All regional leads should review and contribute.",
    };
}

// TODO: Replace with API call to load posts for topic
function buildTestPosts(topicUID: string, currentUser: string): DiscussionPost[] {
    return [
        {uid: "post-1", topicUID, postedAt: new Date(2026, 2, 15, 10, 5).getTime(), postedBy: currentUser, subject: "Draft OPSEC framework - initial proposal", content: "I've drafted the initial OPSEC guidelines based on last quarter's incident reports. The main changes focus on communications protocols and device handling procedures when operating in high-risk zones. Please review the attached framework and flag anything that conflicts with your regional SOP.", AIResponse: false, AIPrompt: ""},
        {uid: "post-2", topicUID, postedAt: new Date(2026, 2, 15, 11, 30).getTime(), postedBy: "K. Andersen", subject: "", content: "Good baseline. One concern — Section 3.2 on encrypted communications assumes everyone has access to the new Tier-2 devices. Our eastern sector teams are still running legacy hardware and won't receive upgrades until Q3.", AIResponse: false, AIPrompt: ""},
        {uid: "post-3", topicUID, postedAt: new Date(2026, 2, 16, 9, 0).getTime(), postedBy: "M. Chen", subject: "Social engineering - additional section needed", content: "Agreed with K. Andersen on the hardware gap. I'd also suggest we add a section on social engineering awareness. We had two near-misses last month where field operatives were targeted through personal social media accounts. The current guidelines don't address digital footprint management at all.", AIResponse: false, AIPrompt: ""},
        {uid: "post-4", topicUID, postedAt: new Date(2026, 2, 16, 14, 15).getTime(), postedBy: "J. Rivera", subject: "", content: "Good points both. I'll add a legacy device appendix for Section 3.2 and draft the social engineering section. @M. Chen — can you send me the incident summaries from those near-misses? I want to use sanitized versions as training examples.", AIResponse: true, AIPrompt: ""},
        {uid: "post-5", topicUID, postedAt: new Date(2026, 3, 1, 8, 45).getTime(), postedBy: "A. Petrov", subject: "Partner agency interoperability", content: "Late to this thread but wanted to flag — the new protocols need to account for joint operations with partner agencies. Their comms standards don't always align with ours, and we've been handling the gap ad hoc. A formal interoperability section would save a lot of confusion in the field.", AIResponse: false, AIPrompt: ""},
        {uid: "post-6", topicUID, postedAt: new Date(2026, 3, 5, 16, 30).getTime(), postedBy: "K. Andersen", subject: "", content: "Updated draft looks solid. The legacy appendix addresses my concern. One more thought: should we require quarterly OPSEC refresher briefings? The guidelines are only useful if people actually internalize them.", AIResponse: false, AIPrompt: "Respond as a senior security analyst. Push back on any proposals that add process overhead without clearly reducing risk."},
        {uid: "post-7", topicUID, postedAt: new Date(2026, 3, 8, 10, 0).getTime(), postedBy: "M. Chen", subject: "Sanitized incident summaries", content: "Here are the sanitized incident summaries from the social engineering near-misses as requested:\n\nIncident 1 (Feb 12): An operative's personal Instagram account was identified through a photo geotagged near a safehouse. The adversary cross-referenced public posts with known operational patterns. No breach occurred, but the operative's cover identity required rotation.\n\nIncident 2 (Feb 28): A phishing email mimicking an internal HR system was sent to three field team members. One clicked the link and entered credentials before realizing the domain was spoofed. Immediate password rotation and session invalidation prevented unauthorized access, but the attacker now had a confirmed valid email address.\n\nRecommendations based on these incidents:\n- Mandatory social media lockdown checklist before deployment\n- Quarterly phishing simulation exercises\n- Hardware security keys for all email accounts (not just SMS 2FA)\n- Establishment of a rapid-response protocol for identity compromise\n\nI can draft the full section if J. Rivera wants to hand that off.", AIResponse: false, AIPrompt: ""},
    ];
}

// TODO: Replace with API call to load votes for topic
function buildTestVotes(topicUID: string, currentUser: string): DiscussionVote[] {
    const t = (m: number, d: number, h = 12) => new Date(2026, m, d, h, 0).getTime();
    const byName = (n: string) => voteTypeByName(n)!;
    return [
        {uid: "vote-1",  topicUID, postUID: "post-1", postedAt: t(2, 16),  postedBy: "K. Andersen", voteType: byName("LIKE")},
        {uid: "vote-2",  topicUID, postUID: "post-1", postedAt: t(2, 17),  postedBy: "M. Chen",     voteType: byName("INSIGHTFUL")},
        {uid: "vote-3",  topicUID, postUID: "post-1", postedAt: t(2, 18),  postedBy: currentUser,   voteType: byName("CELEBRATE")},

        {uid: "vote-4",  topicUID, postUID: "post-2", postedAt: t(2, 16),  postedBy: currentUser,   voteType: byName("LIKE")},

        {uid: "vote-5",  topicUID, postUID: "post-3", postedAt: t(2, 17),  postedBy: "K. Andersen", voteType: byName("LIKE")},
        {uid: "vote-6",  topicUID, postUID: "post-3", postedAt: t(2, 17),  postedBy: "J. Rivera",   voteType: byName("DISAGREE")},

        {uid: "vote-7",  topicUID, postUID: "post-7", postedAt: t(3, 9),   postedBy: "K. Andersen", voteType: byName("LIKE")},
        {uid: "vote-8",  topicUID, postUID: "post-7", postedAt: t(3, 9),   postedBy: "J. Rivera",   voteType: byName("CELEBRATE")},
        {uid: "vote-9",  topicUID, postUID: "post-7", postedAt: t(3, 9),   postedBy: "A. Petrov",   voteType: byName("INSIGHTFUL")},
        {uid: "vote-10", topicUID, postUID: "post-7", postedAt: t(3, 9),   postedBy: currentUser,   voteType: byName("LOVE")},
        {uid: "vote-11", topicUID, postUID: "post-7", postedAt: t(3, 10),  postedBy: "T. Nakamura", voteType: byName("SUPPORT")},

        {uid: "vote-12", topicUID, postUID: "post-1", commentUID: "comment-1", postedAt: t(2, 17), postedBy: currentUser,   voteType: byName("LIKE")},
        {uid: "vote-13", topicUID, postUID: "post-1", commentUID: "comment-1", postedAt: t(2, 17), postedBy: "M. Chen",     voteType: byName("INSIGHTFUL")},
        {uid: "vote-14", topicUID, postUID: "post-1", commentUID: "comment-3", postedAt: t(2, 18), postedBy: "K. Andersen", voteType: byName("LIKE")},
    ];
}

// TODO: Replace with API call to load comments for topic
function buildTestComments(topicUID: string, currentUser: string): DiscussionPostComment[] {
    const t = (m: number, d: number, h = 12) => new Date(2026, m, d, h, 0).getTime();
    return [
        // Root comments on post-1
        {uid: "comment-1", topicUID, postUID: "post-1", postedAt: t(2, 16, 11), postedBy: "K. Andersen", content: "Strong start. I'd add a section on travel-day device setup."},
        {uid: "comment-2", topicUID, postUID: "post-1", postedAt: t(2, 17, 9),  postedBy: "M. Chen", content: "+1 to that. We've had two incidents this quarter from unhardened laptops in transit."},
        // Replies to comment-1 (level 2)
        {uid: "comment-3", topicUID, postUID: "post-1", commentUID: "comment-1", postedAt: t(2, 17, 15), postedBy: currentUser, content: "Good call — drafting that now."},
        {uid: "comment-4", topicUID, postUID: "post-1", commentUID: "comment-1", postedAt: t(2, 18, 8),  postedBy: "K. Andersen", content: "Want me to share the eastern sector's checklist as a starting point?"},
        // Reply to comment-3 (level 3)
        {uid: "comment-5", topicUID, postUID: "post-1", commentUID: "comment-3", postedAt: t(2, 18, 10), postedBy: "K. Andersen", content: "Also — don't forget the BIOS lockdown step. Easy to miss."},

        // Comment on post-3
        {uid: "comment-6", topicUID, postUID: "post-3", postedAt: t(2, 17, 12), postedBy: currentUser, content: "I have those summaries — sending them through after this thread."},

        // Root comments on post-5
        {uid: "comment-7", topicUID, postUID: "post-5", postedAt: t(3, 1, 9),  postedBy: "K. Andersen", content: "Good catch on the interop gap — it's bitten us twice on joint ops."},
        {uid: "comment-8", topicUID, postUID: "post-5", postedAt: t(3, 2, 10), postedBy: "M. Chen", content: "Suggest we loop in the partner liaison group when drafting that section."},
        {uid: "comment-9", topicUID, postUID: "post-5", postedAt: t(3, 2, 14), postedBy: "J. Rivera", content: "Agreed. Will reach out to Sara from PLG this week."},

        // Comment on post-6
        {uid: "comment-10", topicUID, postUID: "post-6", postedAt: t(3, 6, 10), postedBy: "M. Chen", content: "Quarterly is a good cadence. I'd argue for a brief monthly nudge between formal sessions."},
    ];
}

// --- Styles ---

const bubbleSx = {
    background: "linear-gradient(135deg, rgba(9,25,32,0.85) 0%, rgba(20,40,55,0.7) 100%)",
    border: "1px solid #3F464F",
    borderRadius: "12px",
    padding: "10px 14px",
    maxWidth: 760,
    width: "100%",
};

const commentBubbleSx = {
    background: "rgba(9, 25, 32, 0.55)",
    border: "1px solid #2d3640",
    borderRadius: "10px",
    padding: "8px 12px",
    width: "100%",
    maxWidth: 720,
    alignSelf: "flex-start" as const,
};

const authorSx = {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#02cfee",
    textAlign: "left" as const,
};

const timestampSx = {
    fontSize: "0.6rem",
    fontWeight: 400,
    color: "#a8b8c0",
    textAlign: "left" as const,
};

const contentSx = {
    fontSize: "0.8rem",
    fontWeight: 300,
    color: "#F2F5FA",
    lineHeight: 1.7,
    textAlign: "left" as const,
    whiteSpace: "pre-wrap" as const,
};

const subjectSx = {
    fontSize: "0.9rem",
    fontWeight: 700,
    color: "#F2F5FA",
    textAlign: "left" as const,
    lineHeight: 1.4,
};

const composeFieldSx = {
    flex: 1,
    backgroundColor: "rgba(9, 25, 32, 0.6)",
    borderRadius: "6px",
    "& .MuiOutlinedInput-root": {
        borderRadius: "6px",
        color: "#f2f5fa",
        fontSize: "0.8rem",
        "& fieldset": {border: "1px solid rgba(112, 147, 160, 0.35)"},
        "&:hover fieldset": {border: "1px solid #02cfee"},
        "&.Mui-focused fieldset": {border: "1px solid #38ff82", boxShadow: "0 0 6px rgba(56, 255, 130, 0.2)"},
    },
    "& .MuiInputLabel-root": {color: "rgba(242, 245, 250, 0.55)", fontSize: "0.8rem"},
    "& .MuiInputLabel-root.Mui-focused": {color: "#38ff82"},
};

// --- Helpers ---

function formatDateTime(epoch: number): string {
    const d = new Date(epoch);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const h = d.getHours();
    const m = d.getMinutes();
    const meridiem = h >= 12 ? "PM" : "AM";
    const hour = h % 12 || 12;
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${hour}:${String(m).padStart(2, "0")} ${meridiem}`;
}

const AVATAR_COLORS = ["#02cfee", "#38ff82", "#d06440", "#ff3399", "#7093a0", "#8866cc"];

function avatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function initials(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
}

function isAIActive(post: DiscussionPost): boolean {
    return post.AIResponse || post.AIPrompt.trim().length > 0;
}

function hasCustomAIPrompt(post: DiscussionPost): boolean {
    return post.AIPrompt.trim().length > 0;
}

function postHasDescendants(postUID: string, votes: DiscussionVote[], comments: DiscussionPostComment[]): boolean {
    return votes.some(v => v.postUID === postUID) || comments.some(c => c.postUID === postUID);
}

function commentHasDescendants(commentUID: string, votes: DiscussionVote[], comments: DiscussionPostComment[]): boolean {
    return votes.some(v => v.commentUID === commentUID) || comments.some(c => c.commentUID === commentUID);
}

function tallyVotes(votes: DiscussionVote[]): Array<{type: DiscussionVoteType; count: number}> {
    const map = new Map<string, {type: DiscussionVoteType; count: number}>();
    for (const v of votes) {
        const entry = map.get(v.voteType.name);
        if (entry) entry.count++;
        else map.set(v.voteType.name, {type: v.voteType, count: 1});
    }
    return DISCUSSION_VOTE_TYPES
        .filter(t => map.has(t.name))
        .map(t => map.get(t.name)!);
}

// --- Vote section (tally + hover-pickers + reply/comment buttons) ---

interface VoteSectionProps {
    votes: DiscussionVote[];
    currentUser: string;
    showReply?: boolean;      // comment-style: "Reply"
    showComment?: boolean;    // post-style: "Comment"
    onVotePick: (voteType: DiscussionVoteType) => void;
    onRemoveVote?: () => void;
    onOpenComment?: () => void;
    onOpenReply?: () => void;
}

function VoteSection({votes, currentUser, showReply, showComment, onVotePick, onRemoveVote, onOpenComment, onOpenReply}: VoteSectionProps) {
    const userVote = votes.find(v => v.postedBy === currentUser);
    const tally = tallyVotes(votes);
    const userVoteIsLike = userVote?.voteType.likeVote === true;
    const userVoteIsDislike = userVote?.voteType.likeVote === false;

    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{mt: 0.5, rowGap: 0.5, width: "100%"}}
        >
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{rowGap: 0.25}}>
                <VoteHoverButton
                    kind="like"
                    subtypes={LIKE_VOTE_TYPES}
                    userVote={userVoteIsLike ? userVote?.voteType : undefined}
                    onPick={onVotePick}
                />
                <VoteHoverButton
                    kind="dislike"
                    subtypes={DISLIKE_VOTE_TYPES}
                    userVote={userVoteIsDislike ? userVote?.voteType : undefined}
                    onPick={onVotePick}
                />
                {showComment && onOpenComment && (
                    <Button
                        size="small"
                        startIcon={<ChatBubbleOutlineIcon sx={{fontSize: 14}} />}
                        onClick={onOpenComment}
                        sx={voteActionButtonSx}
                    >
                        Comment
                    </Button>
                )}
                {showReply && onOpenReply && (
                    <Button
                        size="small"
                        startIcon={<ReplyIcon sx={{fontSize: 14}} />}
                        onClick={onOpenReply}
                        sx={voteActionButtonSx}
                    >
                        Reply
                    </Button>
                )}
                {userVote && onRemoveVote && (
                    <Tooltip title="Remove your vote">
                        <IconButton size="small" onClick={onRemoveVote} sx={{color: "#7093a0", padding: "4px", "&:hover": {color: "#d83f2f"}}}>
                            <CloseIcon sx={{fontSize: 14}} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
            {tally.length > 0 && (
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    flexWrap="wrap"
                    justifyContent="flex-end"
                    sx={{rowGap: 0.25, marginLeft: "auto"}}
                >
                    {tally.map(({type, count}) => {
                        const Icon = voteIconComponent(type.name, true);
                        const isMine = userVote?.voteType.name === type.name;
                        return (
                            <Tooltip key={type.name} title={`${count} ${type.name.toLowerCase()}${isMine ? " (you)" : ""}`}>
                                <Box sx={{display: "inline-flex", alignItems: "center", gap: 0.3}}>
                                    <Icon sx={{fontSize: 13, color: VOTE_COLORS[type.name] ?? "#a8b8c0"}} />
                                    <Typography sx={{fontSize: "0.65rem", color: isMine ? "#F2F5FA" : "#a8b8c0", fontWeight: isMine ? 600 : 400}}>
                                        {count}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Stack>
            )}
        </Stack>
    );
}

const voteActionButtonSx = {
    fontSize: "0.7rem",
    textTransform: "none" as const,
    color: "#a8b8c0",
    padding: "2px 10px",
    minWidth: 0,
    background: "transparent",
    "&:hover": {background: "rgba(2, 207, 238, 0.08)", color: "#02cfee"},
};

// --- Hover-picker button for Like/Dislike ---

function VoteHoverButton({kind, subtypes, userVote, onPick}: {
    kind: "like" | "dislike";
    subtypes: readonly DiscussionVoteType[];
    userVote?: DiscussionVoteType;
    onPick: (type: DiscussionVoteType) => void;
}) {
    const [open, setOpen] = useState(false);
    const closeTimer = useRef<number | null>(null);

    function clearTimer() {
        if (closeTimer.current != null) {
            window.clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }
    function show() { clearTimer(); setOpen(true); }
    function delayedHide() {
        clearTimer();
        closeTimer.current = window.setTimeout(() => setOpen(false), 200);
    }
    useEffect(() => clearTimer, []);

    const baseLabel = kind === "like" ? "Like" : "Dislike";
    const picked = userVote !== undefined;
    const filled = picked;
    const Icon = userVote
        ? voteIconComponent(userVote.name, true)
        : (kind === "like" ? ThumbUpOutlinedIcon : ThumbDownOutlinedIcon);
    const color = userVote
        ? (VOTE_COLORS[userVote.name] ?? "#02cfee")
        : "#a8b8c0";

    return (
        <Box sx={{position: "relative", display: "inline-block"}} onMouseEnter={show} onMouseLeave={delayedHide}>
            <Button
                size="small"
                startIcon={<Icon sx={{fontSize: 14, color}} />}
                onClick={() => {
                    // Default single-click action: first subtype when idle, or the user's current subtype.
                    onPick(userVote ?? subtypes[0]);
                }}
                sx={{
                    ...voteActionButtonSx,
                    color: picked ? color : "#a8b8c0",
                    fontWeight: filled ? 600 : 400,
                }}
            >
                {userVote ? titleCase(userVote.name) : baseLabel}
            </Button>
            {open && (
                <Box
                    onMouseEnter={show}
                    onMouseLeave={delayedHide}
                    sx={{
                        position: "absolute",
                        bottom: "100%",
                        left: 0,
                        marginBottom: "4px",
                        padding: "6px 8px",
                        background: "#1a2730",
                        border: "1px solid #3F464F",
                        borderRadius: "999px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                        zIndex: 20,
                        display: "flex",
                        gap: 4,
                    }}
                >
                    {subtypes.map(t => {
                        const SubIcon = voteIconComponent(t.name, true);
                        const subColor = VOTE_COLORS[t.name] ?? "#a8b8c0";
                        const isPicked = userVote?.name === t.name;
                        return (
                            <Tooltip key={t.name} title={titleCase(t.name)}>
                                <IconButton
                                    size="small"
                                    onClick={() => { onPick(t); setOpen(false); }}
                                    sx={{
                                        padding: "6px",
                                        color: subColor,
                                        border: isPicked ? `1px solid ${subColor}` : "1px solid transparent",
                                        transition: "transform 0.1s",
                                        "&:hover": {transform: "scale(1.25)", background: "rgba(2, 207, 238, 0.08)"},
                                    }}
                                >
                                    <SubIcon sx={{fontSize: 20}} />
                                </IconButton>
                            </Tooltip>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}

function titleCase(raw: string): string {
    return raw.charAt(0) + raw.slice(1).toLowerCase();
}

// --- Vote / comment composer ---

interface VoteCommentComposerProps {
    voteType?: DiscussionVoteType;    // undefined means comment-only
    onSubmit: (text: string) => void;
    onCancel: () => void;
}

function VoteCommentComposer({voteType, onSubmit, onCancel}: VoteCommentComposerProps) {
    const [text, setText] = useState("");
    const textTrim = text.trim();
    const canSubmit = voteType ? true : textTrim.length > 0;
    const label = voteType ? "Add an optional comment…" : "Write a comment…";
    const Icon = voteType ? voteIconComponent(voteType.name, true) : null;
    const iconColor = voteType ? (VOTE_COLORS[voteType.name] ?? "#02cfee") : "#02cfee";

    function handleKeyDown(e: React.KeyboardEvent) {
        if (e.key === "Escape") { e.preventDefault(); onCancel(); }
        else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            if (canSubmit) onSubmit(textTrim);
        }
    }

    return (
        <Box sx={{
            background: "rgba(9, 25, 32, 0.85)",
            border: `1px solid ${voteType ? iconColor : "rgba(2, 207, 238, 0.35)"}`,
            borderRadius: "10px",
            padding: "10px 14px",
            width: "100%",
        }}>
            <Stack spacing={0.75}>
                {voteType && Icon && (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <Icon sx={{fontSize: 16, color: iconColor}} />
                        <Typography sx={{fontSize: "0.7rem", color: iconColor, fontWeight: 600}}>
                            Voting {titleCase(voteType.name)}
                        </Typography>
                        <Typography sx={{fontSize: "0.65rem", color: "#a8b8c0"}}>
                            · comment optional
                        </Typography>
                    </Stack>
                )}
                <TextField
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={8}
                    autoFocus
                    placeholder={label}
                    value={text}
                    onChange={e => setText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={composeFieldSx}
                    fullWidth
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={onCancel}
                        startIcon={<CloseIcon sx={{fontSize: 14}} />}
                        sx={{
                            fontSize: "0.7rem", textTransform: "none",
                            borderColor: "#7093a0", color: "#a8b8c0",
                            "&:hover": {borderColor: "#d06440", color: "#d06440"},
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => canSubmit && onSubmit(textTrim)}
                        disabled={!canSubmit}
                        startIcon={<SendIcon sx={{fontSize: 14}} />}
                        sx={{
                            ...appStyles.panelActionButton,
                            "&.Mui-disabled": {
                                backgroundColor: "rgba(2, 207, 238, 0.15)",
                                color: "rgba(2, 207, 238, 0.4)",
                            },
                        }}
                    >
                        {voteType ? "Submit" : "Post Comment"}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

// --- Comment node (recursive, unlimited nesting) ---

interface ComposerState {
    postUID: string;
    commentUID?: string;
    voteType?: DiscussionVoteType;
}

interface CommentCallbacks {
    currentUser: string;
    votes: DiscussionVote[];
    allComments: DiscussionPostComment[];
    composer: ComposerState | null;
    onVotePick: (ctx: {postUID: string; commentUID?: string}, voteType: DiscussionVoteType) => void;
    onRemoveVote: (ctx: {postUID: string; commentUID?: string}) => void;
    onOpenReply: (postUID: string, commentUID: string) => void;
    onCancelComposer: () => void;
    onSubmitComposer: (text: string) => void;
    onDeleteComment: (uid: string) => void;
}

function CommentNode({comment, callbacks}: {comment: DiscussionPostComment; callbacks: CommentCallbacks}) {
    const {currentUser, votes, allComments, composer, onVotePick, onRemoveVote, onOpenReply, onCancelComposer, onSubmitComposer, onDeleteComment} = callbacks;
    const [hovered, setHovered] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const childComments = allComments
        .filter(c => c.commentUID === comment.uid)
        .sort((a, b) => a.postedAt - b.postedAt);
    const commentVotes = votes.filter(v => v.commentUID === comment.uid);
    const composerActive = composer !== null
        && composer.postUID === comment.postUID
        && composer.commentUID === comment.uid;
    const canDelete = comment.postedBy === currentUser
        && !commentHasDescendants(comment.uid, votes, allComments);

    return (
        <Stack spacing={0.75} sx={{mb: 0.75}}>
            <Box
                sx={{...commentBubbleSx, position: "relative"}}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Avatar sx={{
                        width: 24, height: 24,
                        fontSize: "0.55rem",
                        fontWeight: 700,
                        backgroundColor: avatarColor(comment.postedBy),
                        color: "#091920",
                    }}>
                        {initials(comment.postedBy)}
                    </Avatar>
                    <Typography sx={authorSx}>{comment.postedBy}</Typography>
                    <Typography sx={timestampSx}>{formatDateTime(comment.postedAt)}</Typography>
                    {canDelete && (
                        <Tooltip title="Delete comment">
                            <IconButton
                                size="small"
                                onClick={() => setConfirmDelete(true)}
                                sx={{
                                    marginLeft: "auto",
                                    opacity: hovered ? 1 : 0,
                                    transition: "opacity 0.15s",
                                    padding: "2px",
                                    color: "#7093a0",
                                    "&:hover": {color: "#d83f2f"},
                                }}
                            >
                                <DeleteIcon sx={{fontSize: 14}} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
                <Typography sx={{...contentSx, fontSize: "0.78rem", marginTop: "4px"}}>
                    {comment.content}
                </Typography>
                <Divider sx={{borderColor: "#2d3640", marginTop: "6px"}} />
                <VoteSection
                    votes={commentVotes}
                    currentUser={currentUser}
                    showReply
                    onVotePick={(type) => onVotePick({postUID: comment.postUID, commentUID: comment.uid}, type)}
                    onRemoveVote={() => onRemoveVote({postUID: comment.postUID, commentUID: comment.uid})}
                    onOpenReply={() => onOpenReply(comment.postUID, comment.uid)}
                />
            </Box>

            {(composerActive || childComments.length > 0) && (
                <Box sx={{
                    paddingLeft: "28px",
                    borderLeft: "2px solid #3F464F",
                    marginLeft: "12px",
                    paddingTop: "4px",
                }}>
                    {composerActive && (
                        <Box sx={{marginBottom: childComments.length > 0 ? "8px" : 0, maxWidth: 720}}>
                            <VoteCommentComposer
                                voteType={composer?.voteType}
                                onSubmit={onSubmitComposer}
                                onCancel={onCancelComposer}
                            />
                        </Box>
                    )}
                    {childComments.map(child => (
                        <CommentNode key={child.uid} comment={child} callbacks={callbacks} />
                    ))}
                </Box>
            )}

            <Dialog
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                slotProps={{paper: {sx: {backgroundColor: "#1a2730", border: "1px solid #3F464F", borderRadius: "12px"}}}}
            >
                <DialogContent>
                    <DialogContentText sx={{color: "#f2f5fa", fontSize: "0.9rem"}}>
                        Are you sure you want to delete this comment?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{padding: "8px 16px 12px"}}>
                    <Button onClick={() => setConfirmDelete(false)} sx={{color: "#7093a0", fontSize: "0.80rem"}}>Cancel</Button>
                    <Button onClick={() => { setConfirmDelete(false); onDeleteComment(comment.uid); }} sx={{color: "#d83f2f", fontSize: "0.80rem"}}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

// --- Post bubble ---

const COLLAPSED_MAX_HEIGHT = 140;

interface PostBubbleProps {
    post: DiscussionPost;
    isEditing: boolean;
    draft: string;
    canEdit: boolean;
    canDelete: boolean;
    currentUser: string;
    postVotes: DiscussionVote[];
    onDraftChange: (v: string) => void;
    onStartEdit: () => void;
    onSaveEdit: () => void;
    onCancelEdit: () => void;
    onDeletePost: () => void;
    onVotePick: (voteType: DiscussionVoteType) => void;
    onRemoveVote: () => void;
    onOpenComment: () => void;
}

function PostBubble(props: PostBubbleProps) {
    const {post, isEditing, draft, canEdit, canDelete, currentUser, postVotes, onDraftChange, onStartEdit, onSaveEdit, onCancelEdit, onDeletePost, onVotePick, onRemoveVote, onOpenComment} = props;
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const measuredRef = useRef<HTMLElement | null>(null);

    function measureRef(el: HTMLElement | null) {
        if (el && !measuredRef.current) {
            measuredRef.current = el;
            if (el.scrollHeight > COLLAPSED_MAX_HEIGHT) setOverflows(true);
        }
    }

    const saveDisabled = draft.trim() === "" || draft === post.content;
    const aiActive = isAIActive(post);
    const aiCustom = hasCustomAIPrompt(post);

    function handleEditKeyDown(e: React.KeyboardEvent) {
        if (e.key === "Escape") { e.preventDefault(); onCancelEdit(); }
        else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            if (!saveDisabled) onSaveEdit();
        }
    }

    return (
        <Box
            sx={{...bubbleSx, position: "relative"}}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography sx={authorSx}>{post.postedBy}</Typography>
                    <Typography sx={timestampSx}>{formatDateTime(post.postedAt)}</Typography>
                    {aiActive && (
                        <Tooltip title={aiCustom ? `AI enabled with custom prompt: "${post.AIPrompt}"` : "AI response enabled"}>
                            <Chip
                                size="small"
                                icon={<SmartToyOutlinedIcon sx={{fontSize: 12, color: "#38ff82 !important"}} />}
                                label={aiCustom ? "AI · custom" : "AI"}
                                sx={{
                                    height: 18,
                                    fontSize: "0.6rem",
                                    fontWeight: 600,
                                    color: "#38ff82",
                                    backgroundColor: "rgba(56, 255, 130, 0.08)",
                                    border: "1px solid rgba(56, 255, 130, 0.3)",
                                    "& .MuiChip-label": {padding: "0 6px"},
                                }}
                            />
                        </Tooltip>
                    )}
                    {canEdit && !isEditing && (
                        <Tooltip title="Edit post">
                            <IconButton
                                size="small"
                                onClick={onStartEdit}
                                sx={{
                                    marginLeft: "auto",
                                    opacity: hovered ? 1 : 0,
                                    transition: "opacity 0.15s",
                                    padding: "4px",
                                    color: "#7093a0",
                                    "&:hover": {color: "#02cfee"},
                                }}
                            >
                                <EditIcon sx={{fontSize: 16}} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>

                {isEditing ? (
                    <>
                        <TextField
                            multiline
                            minRows={6}
                            maxRows={20}
                            size="small"
                            value={draft}
                            onChange={e => onDraftChange(e.target.value)}
                            onKeyDown={handleEditKeyDown}
                            autoFocus
                            sx={{...composeFieldSx, width: "100%"}}
                        />
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{pt: 0.5}}>
                            <Tooltip title={canDelete ? "Delete post" : "Posts with votes or comments cannot be deleted"}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => setConfirmDelete(true)}
                                        disabled={!canDelete}
                                        sx={{
                                            padding: "4px",
                                            color: "#7093a0",
                                            "&:hover": {color: "#d83f2f"},
                                            "&.Mui-disabled": {color: "rgba(112, 147, 160, 0.35)"},
                                        }}
                                    >
                                        <DeleteIcon sx={{fontSize: 18}} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Stack direction="row" spacing={0.5}>
                                <Tooltip title="Cancel (Esc)">
                                    <IconButton
                                        size="small"
                                        onClick={onCancelEdit}
                                        sx={{padding: "4px", color: "#7093a0", "&:hover": {color: "#d83f2f"}}}
                                    >
                                        <CloseIcon sx={{fontSize: 18}} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={saveDisabled ? "No changes to save" : "Save (Ctrl+Enter)"}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={onSaveEdit}
                                            disabled={saveDisabled}
                                            sx={{
                                                padding: "4px",
                                                color: "#02cfee",
                                                "&:hover": {color: "#38ff82"},
                                                "&.Mui-disabled": {color: "rgba(2, 207, 238, 0.3)"},
                                            }}
                                        >
                                            <CheckIcon sx={{fontSize: 18}} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </>
                ) : (
                    <>
                        {post.subject && post.subject.trim().length > 0 && (
                            <Typography sx={subjectSx}>{post.subject}</Typography>
                        )}
                        <Collapse in={expanded} collapsedSize={COLLAPSED_MAX_HEIGHT}>
                            <Typography ref={measureRef} sx={contentSx}>{post.content}</Typography>
                        </Collapse>
                        {overflows && (
                            <Typography
                                onClick={() => setExpanded(prev => !prev)}
                                sx={{
                                    fontSize: "0.65rem",
                                    fontWeight: 600,
                                    color: "#02cfee",
                                    cursor: "pointer",
                                    textAlign: "left",
                                    "&:hover": {textDecoration: "underline"},
                                }}
                            >
                                {expanded ? "show less" : "show more"}
                            </Typography>
                        )}
                        <Divider sx={{borderColor: "#2d3640", marginTop: "6px"}} />
                        <VoteSection
                            votes={postVotes}
                            currentUser={currentUser}
                            showComment
                            onVotePick={onVotePick}
                            onRemoveVote={onRemoveVote}
                            onOpenComment={onOpenComment}
                        />
                    </>
                )}
            </Stack>

            <Dialog
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                slotProps={{paper: {sx: {backgroundColor: "#1a2730", border: "1px solid #3F464F", borderRadius: "12px"}}}}
            >
                <DialogContent>
                    <DialogContentText sx={{color: "#f2f5fa", fontSize: "0.90rem"}}>
                        Are you sure you want to delete this post?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{padding: "8px 16px 12px"}}>
                    <Button onClick={() => setConfirmDelete(false)} sx={{color: "#7093a0", fontSize: "0.80rem"}}>Cancel</Button>
                    <Button onClick={() => { setConfirmDelete(false); onDeletePost(); }} sx={{color: "#d83f2f", fontSize: "0.80rem"}}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// --- Thread entry (spine + bubble + below-bubble children) ---

interface ThreadEntryProps {
    post: DiscussionPost;
    isEditing: boolean;
    draft: string;
    canEdit: boolean;
    canDelete: boolean;
    onDraftChange: (v: string) => void;
    onStartEdit: () => void;
    onSaveEdit: () => void;
    onCancelEdit: () => void;
    onDeletePost: () => void;
    votes: DiscussionVote[];
    allComments: DiscussionPostComment[];
    composer: ComposerState | null;
    currentUser: string;
    onVotePick: (ctx: {postUID: string; commentUID?: string}, voteType: DiscussionVoteType) => void;
    onRemoveVote: (ctx: {postUID: string; commentUID?: string}) => void;
    onOpenComment: (postUID: string) => void;
    onOpenReply: (postUID: string, commentUID: string) => void;
    onCancelComposer: () => void;
    onSubmitComposer: (text: string) => void;
    onDeleteComment: (uid: string) => void;
}

function ThreadEntry(props: ThreadEntryProps) {
    const {post, isEditing, draft, canEdit, canDelete, onDraftChange, onStartEdit, onSaveEdit, onCancelEdit, onDeletePost, votes, allComments, composer, currentUser, onVotePick, onRemoveVote, onOpenComment, onOpenReply, onCancelComposer, onSubmitComposer, onDeleteComment} = props;
    const color = avatarColor(post.postedBy);
    const postVotes = votes.filter(v => v.postUID === post.uid && !v.commentUID);
    const postRootComments = allComments
        .filter(c => c.postUID === post.uid && !c.commentUID)
        .sort((a, b) => a.postedAt - b.postedAt);
    const composerActive = composer !== null && composer.postUID === post.uid && !composer.commentUID;

    const callbacks: CommentCallbacks = {
        currentUser,
        votes,
        allComments,
        composer,
        onVotePick,
        onRemoveVote,
        onOpenReply,
        onCancelComposer,
        onSubmitComposer,
        onDeleteComment,
    };

    return (
        <Stack direction="row" alignItems="flex-start" sx={{width: "100%"}}>
            <Box sx={{
                width: SPINE_COL,
                minWidth: SPINE_COL,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: "8px",
            }}>
                <Avatar sx={{
                    width: AVATAR_SIZE,
                    height: AVATAR_SIZE,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    backgroundColor: color,
                    color: "#091920",
                    border: "3px solid #3F464F",
                    zIndex: 1,
                }}>
                    {initials(post.postedBy)}
                </Avatar>
            </Box>
            <Stack direction="column" sx={{flex: 1, minWidth: 0}} spacing={1}>
                <Box sx={{display: "flex", alignItems: "flex-start", paddingTop: "12px"}}>
                    <Box sx={{width: 16, minWidth: 16, height: 2, backgroundColor: "#3F464F", marginTop: "8px", marginLeft: `-${CONNECT_OVERLAP}px`}} />
                    <Stack direction="column" spacing={0.75} sx={{flex: 1, minWidth: 0}}>
                        <PostBubble
                            post={post}
                            isEditing={isEditing}
                            draft={draft}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onDraftChange={onDraftChange}
                            onStartEdit={onStartEdit}
                            onSaveEdit={onSaveEdit}
                            onCancelEdit={onCancelEdit}
                            onDeletePost={onDeletePost}
                            currentUser={currentUser}
                            postVotes={postVotes}
                            onVotePick={(type) => onVotePick({postUID: post.uid}, type)}
                            onRemoveVote={() => onRemoveVote({postUID: post.uid})}
                            onOpenComment={() => onOpenComment(post.uid)}
                        />
                        {composerActive && (
                            <Box sx={{paddingLeft: "4px", maxWidth: 760}}>
                                <VoteCommentComposer
                                    voteType={composer?.voteType}
                                    onSubmit={onSubmitComposer}
                                    onCancel={onCancelComposer}
                                />
                            </Box>
                        )}
                        {postRootComments.length > 0 && (
                            <Box sx={{paddingLeft: "20px", paddingTop: "4px"}}>
                                {postRootComments.map(c => (
                                    <CommentNode key={c.uid} comment={c} callbacks={callbacks} />
                                ))}
                            </Box>
                        )}
                    </Stack>
                </Box>
            </Stack>
        </Stack>
    );
}

// --- Topic header ---

function TopicHeader({topic}: {topic: DiscussionTopic}) {
    const hasTitle = topic.title.trim().length > 0;
    return (
        <Stack spacing={0.5} sx={{padding: "16px 20px", width: "100%", alignItems: "flex-start"}}>
            {hasTitle && (
                <Typography sx={{fontSize: "1.2rem", fontWeight: 700, color: "#F2F5FA", textAlign: "left"}}>
                    {topic.title}
                </Typography>
            )}
            <Typography sx={{fontSize: "0.85rem", fontWeight: 300, color: "rgba(242, 245, 250, 0.8)", lineHeight: 1.6, textAlign: "left"}}>
                {topic.description}
            </Typography>
        </Stack>
    );
}

// --- Main component ---

const UPDATED_TAG_RE = /\s*\n*\(updated [^)]+\)\s*$/;

function stripUpdatedTag(content: string): string {
    return content.replace(UPDATED_TAG_RE, "");
}

function UXDiscussionViewer() {
    const {networkUID, topicUID} = useParams();
    const [topic, setTopic] = useState<DiscussionTopic | null>(null);
    const [posts, setPosts] = useState<DiscussionPost[]>([]);
    const [votes, setVotes] = useState<DiscussionVote[]>([]);
    const [comments, setComments] = useState<DiscussionPostComment[]>([]);

    // New post compose
    const [newSubject, setNewSubject] = useState("");
    const [newContent, setNewContent] = useState("");
    const [newAllowAI, setNewAllowAI] = useState(false);
    const [newAIPrompt, setNewAIPrompt] = useState("");
    const [composeExpanded, setComposeExpanded] = useState(false);

    // Post edit
    const [editingPostUID, setEditingPostUID] = useState<string | null>(null);
    const [editDraft, setEditDraft] = useState("");

    // Vote/comment composer
    const [composer, setComposer] = useState<ComposerState | null>(null);

    useEffect(() => {
        // TODO: Replace with API calls
        const currentUser = appSession.getUsername() ?? DEV_USER;
        const t = buildTestTopic(currentUser);
        setTopic(t);
        setPosts(buildTestPosts(t.uid, currentUser));
        setVotes(buildTestVotes(t.uid, currentUser));
        setComments(buildTestComments(t.uid, currentUser));
    }, [networkUID, topicUID]);

    const currentUser = appSession.getUsername() ?? DEV_USER;

    const sorted = [...posts].sort((a, b) => b.postedAt - a.postedAt);

    function handleNewPost() {
        const content = newContent.trim();
        if (!content || !topic) return;
        // TODO: Replace with API call to create post
        const post: DiscussionPost = {
            uid: `post-new-${Date.now()}`,
            topicUID: topic.uid,
            postedAt: Date.now(),
            postedBy: currentUser,
            subject: newSubject.trim() || undefined,
            content,
            AIResponse: newAllowAI,
            AIPrompt: newAIPrompt.trim(),
        };
        setPosts(prev => [...prev, post]);
        setNewSubject("");
        setNewContent("");
        setNewAllowAI(false);
        setNewAIPrompt("");
        setComposeExpanded(false);
    }

    function handleNewPostKeyDown(e: React.KeyboardEvent) {
        if (e.key === "Enter" && !e.shiftKey && (e.target as HTMLElement).tagName !== "TEXTAREA") {
            e.preventDefault();
            handleNewPost();
        }
    }

    function handleStartEdit(uid: string) {
        const post = posts.find(p => p.uid === uid);
        setEditingPostUID(uid);
        setEditDraft(post?.content ?? "");
    }

    function handleSaveEdit() {
        if (!editingPostUID) return;
        const original = posts.find(p => p.uid === editingPostUID);
        if (!original) return;
        const draftStripped = stripUpdatedTag(editDraft).trim();
        if (!draftStripped) return;
        const originalStripped = stripUpdatedTag(original.content).trim();
        if (draftStripped === originalStripped) {
            setEditingPostUID(null);
            setEditDraft("");
            return;
        }
        // TODO: Replace with API call to update post content
        const stamped = `${draftStripped}\n\n(updated ${formatDateTime(Date.now())})`;
        setPosts(prev => prev.map(p => p.uid === editingPostUID ? {...p, content: stamped} : p));
        setEditingPostUID(null);
        setEditDraft("");
    }

    function handleCancelEdit() {
        setEditingPostUID(null);
        setEditDraft("");
    }

    function handleDeletePost(uid: string) {
        // Delete-gate is enforced by the PostBubble via canDelete prop, but double-check.
        if (postHasDescendants(uid, votes, comments)) return;
        // TODO: Replace with API call to delete post
        setPosts(prev => prev.filter(p => p.uid !== uid));
        if (editingPostUID === uid) {
            setEditingPostUID(null);
            setEditDraft("");
        }
    }

    function handleDeleteComment(uid: string) {
        if (commentHasDescendants(uid, votes, comments)) return;
        // TODO: Replace with API call to delete comment
        setComments(prev => prev.filter(c => c.uid !== uid));
    }

    function handleVotePick(ctx: {postUID: string; commentUID?: string}, voteType: DiscussionVoteType) {
        // Open the vote-comment composer with this vote preselected.
        // On submit, any prior vote from currentUser on the same target is replaced.
        setComposer({postUID: ctx.postUID, commentUID: ctx.commentUID, voteType});
    }

    function handleRemoveVote(ctx: {postUID: string; commentUID?: string}) {
        // TODO: Replace with API call to delete vote
        setVotes(prev => prev.filter(v => !(
            v.postUID === ctx.postUID
            && v.commentUID === ctx.commentUID
            && v.postedBy === currentUser
        )));
        // Close composer if it was targeted at this vote
        if (composer
            && composer.postUID === ctx.postUID
            && composer.commentUID === ctx.commentUID) {
            setComposer(null);
        }
    }

    function handleOpenComment(postUID: string) {
        setComposer({postUID, voteType: undefined});
    }

    function handleOpenReply(postUID: string, commentUID: string) {
        setComposer({postUID, commentUID, voteType: undefined});
    }

    function handleCancelComposer() {
        setComposer(null);
    }

    function handleSubmitComposer(text: string) {
        if (!composer || !topic) return;
        const now = Date.now();

        // If a vote is being cast, replace any prior vote on this target by the same user.
        if (composer.voteType) {
            const voteType = composer.voteType;
            setVotes(prev => {
                const withoutPrior = prev.filter(v => !(
                    v.postUID === composer.postUID
                    && v.commentUID === composer.commentUID
                    && v.postedBy === currentUser
                ));
                // TODO: Replace with API call to create vote
                const newVote: DiscussionVote = {
                    uid: `vote-new-${now}`,
                    topicUID: topic.uid,
                    postUID: composer.postUID,
                    commentUID: composer.commentUID,
                    postedAt: now,
                    postedBy: currentUser,
                    voteType,
                };
                return [...withoutPrior, newVote];
            });
        }

        // Add a comment if text was provided (always required for comment-only, optional for vote).
        if (text.trim().length > 0) {
            // TODO: Replace with API call to create comment
            const newComment: DiscussionPostComment = {
                uid: `comment-new-${now}`,
                topicUID: topic.uid,
                postUID: composer.postUID,
                commentUID: composer.commentUID,
                postedAt: now,
                postedBy: currentUser,
                content: text,
            };
            setComments(prev => [...prev, newComment]);
        }

        setComposer(null);
    }

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {!appSession.activeSession() ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%">
                    <WidgetHeader
                        title="Discussions"
                        breadcrumbs={[
                            {display: "Networks", linkTo: "/networks/", activeView: false},
                            {display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Discussions", linkTo: `/networks/${networkUID}/discussions`, activeView: false},
                            {display: topic?.title?.trim() || topic?.description?.slice(0, 40) || String(topicUID), linkTo: "", activeView: true},
                        ]}
                    />
                    <Stack sx={appStyles.sectionBox} spacing={0} width="100%" alignItems="center">
                        {topic && <TopicHeader topic={topic} />}

                        <Typography sx={{...appStyles.showCountText, padding: "0 20px 8px", width: "100%", textAlign: "center"}}>
                            {posts.length} {posts.length === 1 ? "post" : "posts"}
                        </Typography>

                        <Divider sx={{borderColor: "#3F464F", width: "100%"}} />

                        {/* Compose area */}
                        <Box sx={{width: "100%", maxWidth: 900, padding: "16px 20px 8px"}}>
                            <Stack spacing={1}>
                                <TextField
                                    size="small"
                                    label="Subject (optional)"
                                    value={newSubject}
                                    onChange={e => setNewSubject(e.target.value)}
                                    sx={{...composeFieldSx, width: "100%"}}
                                />
                                <Box sx={{position: "relative"}}>
                                    <TextField
                                        multiline
                                        minRows={composeExpanded ? 12 : 4}
                                        maxRows={composeExpanded ? 20 : 6}
                                        size="small"
                                        label="Write a post..."
                                        value={newContent}
                                        onChange={e => setNewContent(e.target.value)}
                                        onKeyDown={handleNewPostKeyDown}
                                        sx={{...composeFieldSx, width: "100%"}}
                                    />
                                    <Tooltip title={composeExpanded ? "Collapse post area" : "Expand post area"} placement="right">
                                        <IconButton
                                            size="small"
                                            onClick={() => setComposeExpanded(prev => !prev)}
                                            sx={{
                                                position: "absolute",
                                                bottom: 6,
                                                left: 6,
                                                color: "#7093a0",
                                                opacity: 0.6,
                                                "&:hover": {color: "#02cfee", opacity: 1},
                                            }}
                                        >
                                            {composeExpanded ? <UnfoldLessIcon sx={{fontSize: 24}} /> : <UnfoldMoreIcon sx={{fontSize: 24}} />}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={newAllowAI}
                                                onChange={e => setNewAllowAI(e.target.checked)}
                                                sx={{
                                                    color: "#7093a0",
                                                    "&.Mui-checked": {color: "#38ff82"},
                                                }}
                                            />
                                        }
                                        label={
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <SmartToyOutlinedIcon sx={{fontSize: 16, color: "#38ff82"}} />
                                                <Typography sx={{fontSize: "0.75rem", color: "#F2F5FA"}}>
                                                    Allow AI response to comments
                                                </Typography>
                                            </Stack>
                                        }
                                    />
                                    <TextField
                                        size="small"
                                        label="AI Prompt (optional - overrides toggle)"
                                        placeholder="Custom instruction for the AI when responding"
                                        value={newAIPrompt}
                                        onChange={e => setNewAIPrompt(e.target.value)}
                                        multiline
                                        minRows={1}
                                        maxRows={4}
                                        sx={{...composeFieldSx, minWidth: 300, flex: 1}}
                                    />
                                </Stack>
                                <Stack direction="row" justifyContent="flex-end" sx={{pt: 0.5}}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleNewPost}
                                        disabled={!newContent.trim()}
                                        sx={{
                                            ...appStyles.panelActionButton,
                                            minWidth: "auto",
                                            padding: "6px 20px",
                                            "&.Mui-disabled": {
                                                backgroundColor: "rgba(2, 207, 238, 0.15)",
                                                color: "rgba(2, 207, 238, 0.4)",
                                            },
                                        }}
                                        endIcon={<SendIcon sx={{fontSize: 16}} />}
                                    >
                                        Post
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>

                        {sorted.length > 0 ? (
                            <Box sx={{
                                width: "100%",
                                maxWidth: 900,
                                position: "relative",
                                padding: "8px 20px 20px",
                            }}>
                                <Box sx={{
                                    position: "absolute",
                                    left: `${20 + SPINE_COL / 2 - 1}px`,
                                    top: 0,
                                    bottom: 0,
                                    width: 2,
                                    backgroundColor: "#3F464F",
                                    zIndex: 0,
                                }} />

                                <Stack spacing={1}>
                                    {sorted.map(post => {
                                        const canEdit = post.postedBy === currentUser;
                                        const canDelete = canEdit && !postHasDescendants(post.uid, votes, comments);
                                        return (
                                            <ThreadEntry
                                                key={post.uid}
                                                post={post}
                                                isEditing={editingPostUID === post.uid}
                                                draft={editingPostUID === post.uid ? editDraft : ""}
                                                canEdit={canEdit}
                                                canDelete={canDelete}
                                                onDraftChange={setEditDraft}
                                                onStartEdit={() => handleStartEdit(post.uid)}
                                                onSaveEdit={handleSaveEdit}
                                                onCancelEdit={handleCancelEdit}
                                                onDeletePost={() => handleDeletePost(post.uid)}
                                                votes={votes}
                                                allComments={comments}
                                                composer={composer}
                                                currentUser={currentUser}
                                                onVotePick={handleVotePick}
                                                onRemoveVote={handleRemoveVote}
                                                onOpenComment={handleOpenComment}
                                                onOpenReply={handleOpenReply}
                                                onCancelComposer={handleCancelComposer}
                                                onSubmitComposer={handleSubmitComposer}
                                                onDeleteComment={handleDeleteComment}
                                            />
                                        );
                                    })}
                                </Stack>
                            </Box>
                        ) : (
                            <Typography sx={{color: "#a8b8c0", fontSize: "0.85rem", padding: "32px 0"}}>
                                No posts yet. Start the conversation above.
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default UXDiscussionViewer;
